import type { Translation } from "./en";

export const id: Translation = {
  common: {
    required: "Wajib diisi",
    all: "Semua",
    search: "Cari",
    details: "Detail",
    open: "Buka",
    unknown: "Tidak diketahui",
    save: "Simpan",
    saveChanges: "Simpan perubahan",
    cancel: "Batal",
    create: "Buat",
    edit: "Ubah",
    delete: "Hapus",
    remove: "Lepas",
    close: "Tutup",
    refresh: "Muat ulang",
    copy: "Salin",
    copied: "Tersalin",
    loading: "Memuat…",
    actions: "Aksi",
    status: "Status",
    name: "Nama",
    created: "Dibuat",
    updated: "Diubah",
    none: "Tidak ada",
    never: "Belum pernah",
    active: "Aktif",
    disabled: "Nonaktif",
    optional: "Opsional",
    loadMore: "Muat lagi",
    retry: "Coba lagi",
    id: "ID",
    set: "Terisi",
    missing: "Kosong",
    yes: "Ya",
    no: "Tidak",
  },
  nav: {
    profiles: "Profil credential",
    menu: "Menu",
    groupMonitor: "Pantau",
    groupSetup: "Konfigurasi",
    overview: "Ringkasan",
    customers: "Customer",
    logs: "Log pengiriman",
    account: "Akun",
    groupWorkspace: "Ruang kerja",
    groupDelivery: "Pengiriman",
    groupAdmin: "Administrasi",
    apps: "App",
    credentials: "Credential",
    providers: "Provider",
    audit: "Audit log",
    settings: "Pengaturan",
    apiDocs: "Referensi API",
    adminDocs: "Referensi API admin",
    signOut: "Keluar",
    language: "Bahasa",
    theme: "Tema",
    light: "Tema terang",
    dark: "Tema gelap",
    superadmin: "Admin platform",
    groupMessaging: "Pesan",
    send: "Kirim pesan",
  },
  auth: {
    title: "Masuk",
    subtitle: "Kelola app, credential, dan pengiriman di {{app}}.",
    channels: "FCM · APNs · Web Push · Email · SMS · WhatsApp",
    email: "Email",
    emailPlaceholder: "kamu@perusahaan.com",
    password: "Password",
    submit: "Masuk",
  },
  apps: {
    profile: "Profil credential",
    newProfile: "Profil baru untuk app ini",
    profileHint:
      "Pilih profil yang sudah ada untuk berbagi credential-nya, atau mulai dengan profil baru yang kosong.",
    title: "App",
    subtitle: "Setiap app punya API key, credential, dan riwayat pesan sendiri.",
    new: "App baru",
    createTitle: "Buat app",
    name: "Nama app",
    namePlaceholder: "Aiyo Mobile",
    slug: "Slug",
    slugHint: "Huruf kecil, angka, dan tanda hubung. Dibuat dari nama kalau kosong.",
    empty: "Belum ada app",
    emptyHint: "Buat app untuk mendapatkan API key dan mulai mengirim.",
    suspend: "Tangguhkan app",
    activate: "Aktifkan app",
    suspendedHint: "App yang ditangguhkan menolak semua request API sampai diaktifkan lagi.",
    dangerZone: "Zona berbahaya",
    deleteHint: "Hanya app yang belum pernah mengirim pesan yang bisa dihapus.",
    deleteTitle: "Hapus app ini?",
    deleteBody: "API key dan assignment credential-nya ikut terhapus. Tindakan ini tidak bisa dibatalkan.",
  },
  appDetail: {
    activity: "7 hari terakhir",
    openOverview: "Ringkasan",
    openLog: "Log pengiriman",
    openCustomers: "Customer",
    step1: "1. Daftarkan pengguna dan alamatnya (sekali, dan setiap kali berubah)",
    step2: "2. Kirim ke pengguna lewat ID customer",
    overview: "Ringkasan",
    keys: "API key",
    credentials: "Profil credential",
    messages: "Pesan",
    appId: "ID app",
    details: "Detail",
    quickStart: "Kirim pesan pertama",
    quickStartHint: "Buat API key, assign credential untuk setiap channel, lalu panggil API:",
    sendTest: "Kirim pesan",
  },
  keys: {
    new: "API key baru",
    createTitle: "Buat API key",
    name: "Nama key",
    namePlaceholder: "backend-production",
    mode: "Mode",
    live: "Live",
    test: "Test",
    modeHint: "Key test tidak pernah menghubungi provider. Semua pesan masuk ke adapter mock.",
    scopes: "Scope",
    prefix: "Key",
    lastUsed: "Terakhir dipakai",
    revoked: "Dicabut",
    revoke: "Cabut",
    revokeTitle: "Cabut key ini?",
    revokeBody: "Request dengan key ini langsung gagal. Tindakan ini tidak bisa dibatalkan.",
    revokedToast: "API key dicabut",
    createdTitle: "Salin API key sekarang",
    createdBody: "Key lengkap hanya ditampilkan sekali ini. Simpan di secret manager.",
    empty: "Belum ada API key",
  },
  assignments: {
    subtitle:
      "Credential yang boleh dipakai app ini. Klien memilih lewat alias. Tanpa alias, dipakai nomor prioritas terkecil per channel, dan prioritas berikutnya mengambil alih saat provider down.",
    add: "Tambah credential",
    editTitle: "Ubah credential di profil",
    alias: "Alias",
    aliasHint: "Nama tetap yang dikirim klien sebagai `credential`, misal email-main.",
    priority: "Prioritas",
    priorityHint: "1 dicoba pertama.",
    credential: "Credential",
    chooseCredential: "Pilih credential",
    channel: "Channel",
    active: "Aktif",
    empty: "Belum ada credential di profil ini",
    emptyHint: "Tambahkan minimal satu credential untuk setiap channel yang dipakai app.",
    noCredentials: "Kamu belum punya credential.",
    goToCredentials: "Buat credential",
    removeTitle: "Keluarkan dari profil ini?",
    removeBody: "Semua app yang memakai profil ini akan menolak request yang menyebut alias ini.",
    swapHint: "Mengganti credential tetap memakai alias yang sama, jadi klien tidak perlu diubah.",
  },
  credentials: {
    profilesCount: "Profil",
    appsCount: "App",
    inUseHint: "Keluarkan dari semua profil credential sebelum menghapusnya.",
    title: "Credential",
    subtitle: "Akun di setiap provider. Rahasia dienkripsi dan tidak ditampilkan lagi setelah disimpan.",
    new: "Credential baru",
    createTitle: "Buat credential",
    editTitle: "Ubah credential",
    provider: "Provider",
    chooseProvider: "Pilih provider",
    name: "Nama credential",
    namePlaceholder: "SES production",
    assignedApps: "App",
    health: "Uji terakhir",
    test: "Uji koneksi",
    testOk: "Koneksi berhasil",
    testFailed: "Koneksi gagal",
    notChecked: "Belum diuji",
    secretSet: "Tersimpan. Kosongkan untuk memakai nilai yang sekarang.",
    empty: "Belum ada credential",
    emptyHint: "Masukkan detail akun provider, lalu assign ke app.",
    deleteTitle: "Hapus credential ini?",
    deleteBody: "Hanya credential yang tidak di-assign ke app mana pun yang bisa dihapus.",
    config: "Konfigurasi",
    saved: "Credential disimpan",
    disableHint: "Credential nonaktif dilewati dan prioritas berikutnya mengambil alih.",
  },
  providers: {
    title: "Provider",
    subtitle:
      "Status provider berlaku untuk semua organisasi. Nonaktifkan provider saat gangguan, trafik pindah ke credential prioritas berikutnya.",
    code: "Kode",
    channel: "Channel",
    note: "Catatan",
    notePlaceholder: "Insiden atau alasan",
    setStatus: "Ubah status",
    superadminOnly: "Hanya admin platform yang bisa mengubah status provider.",
    mock: "Mock",
    statusHelp: {
      active: "Dipakai seperti biasa.",
      degraded: "Dipakai hanya kalau tidak ada credential sehat lain.",
      disabled: "Tidak pernah dipakai. Credential provider ini dilewati.",
    },
    updated: "Provider diubah",
  },
  messages: {
    message: "Pesan",
    empty: "Belum ada pesan",
    emptyHint: "Kirim lewat API atau tombol Kirim pesan.",
    sendTitle: "Kirim pesan",
    sendHint: "Body sama dengan POST /v1/messages.",
    body: "Body request (JSON)",
    testMode: "Mode test: pakai adapter mock, provider tidak dihubungi",
    channels: "Channel",
    channel: "Channel",
    progress: "Progres",
    priority: "Prioritas",
    category: "Kategori",
    mode: "Mode",
    createdAt: "Dibuat",
    completedAt: "Selesai",
    perChannel: "Per channel",
    deliveries: "Delivery",
    address: "Alamat",
    attempts: "Percobaan",
    provider: "Provider",
    alias: "Alias",
    error: "Error",
    events: "Event",
    rendered: "Payload yang dikirim",
    metadata: "Metadata",
    providerMessageId: "ID pesan di provider",
    autoRefresh: "Diperbarui otomatis selama pesan diproses.",
    deliveryTitle: "Delivery",
    invalidJson: "Body bukan JSON yang valid.",
    duplicate: "Duplikat",
    audience: "Penerima",
    audienceAll: "Semua customer (broadcast)",
    audienceCustomers: "Customer: {{count}}",
    audienceAddresses: "Alamat langsung: {{count}}",
    content: "Konten",
    contentHint: "Yang dilihat penerima di tiap channel.",
    counts: {
      total: "Total",
      pending: "Menunggu",
      sent: "Terkirim",
      delivered: "Diterima",
      failed: "Gagal",
      skipped: "Dilewati",
    },
  },
  settings: {
    tabs: {
      general: "Umum",
      access: "Akses dashboard",
      delivery: "Pengiriman",
      environment: "Server",
    },
    discard: "Batalkan perubahan",
    unsavedTab: "Ada perubahan yang belum disimpan",
    title: "Pengaturan",
    subtitle:
      "Pengaturan platform berlaku untuk semua app dan sampai ke semua server dalam 10 detik, tanpa restart.",
    superadminOnly: "Hanya admin platform yang bisa melihat dan mengubah pengaturan.",
    saved: "Pengaturan disimpan",
    unsaved: "Ada perubahan yang belum disimpan.",
    lastUpdated: "Terakhir diubah {{when}} oleh {{who}}",
    neverUpdated: "Memakai nilai bawaan. Belum ada yang diubah.",
    general: "Umum",
    publicUrl: "URL publik",
    publicUrlHint: "Alamat yang dipakai klien untuk menghubungi API. Tampil di referensi API.",
    logLevel: "Level log",
    logLevelHint: "Berlaku untuk API dan semua worker.",
    mapStyleLight: "Gaya peta, tema terang",
    mapStyleDark: "Gaya peta, tema gelap",
    mapStyleHint: "URL style MapLibre untuk peta customer. Kosongkan untuk memakai OpenFreeMap.",
    access: "Akses dashboard",
    trustedOrigins: "Origin dashboard yang diizinkan",
    trustedOriginsHint:
      "Satu origin per baris, misal https://dashboard.example.com. Origin ini boleh memanggil API admin dengan session.",
    envOrigins: "Selalu diizinkan (diatur di environment server)",
    sessionTtl: "Umur session (jam)",
    sessionTtlHint: "Berlaku untuk login berikutnya.",
    delivery: "Pengiriman",
    providerTimeout: "Batas waktu provider (ms)",
    providerTimeoutHint: "Lama maksimal satu panggilan ke provider sebelum dicoba ulang.",
    lanes: "Lane prioritas",
    lanesHint:
      "Setiap prioritas punya antrean sendiri, jadi broadcast besar tidak menunda OTP. Jeda sebuah lane untuk menahan pesannya, misalnya saat provider sedang gangguan.",
    laneRunning: "Berjalan",
    lanePaused: "Dijeda",
    laneConcurrency: "Kirim paralel",
    pausedWarning:
      "Pesan di lane yang dijeda tetap diterima dan menunggu di antrean. Pesan dikirim begitu lane berjalan lagi.",
    concurrencyHint:
      "Kirim paralel berlaku per worker. Perubahan diterapkan ke worker yang berjalan dalam beberapa detik, tanpa restart.",
    environment: "Environment server",
    environmentHint:
      "Nilai ini dibaca dari environment server karena dibutuhkan sebelum database tersedia atau berbahaya kalau diubah dari sini. Ubah di deployment lalu restart.",
    env: {
      nodeEnv: "Environment",
      port: "Port API",
      poolMax: "Koneksi database",
      databaseUrl: "URL database",
      masterKey: "Kunci enkripsi credential",
      keyVersion: "Versi kunci enkripsi",
      cookieSecure: "Cookie session Secure",
      cookieSameSite: "Cookie SameSite",
      origins: "Origin dashboard",
    },
  },
  overview: {
    title: "Ringkasan",
    subtitle: "Kondisi pengiriman notifikasi di semua app.",
    allApps: "Semua app",
    app: "App",
    period: "Periode",
    periods: { "1": "24 jam", "7": "7 hari", "30": "30 hari", "90": "90 hari" },
    includeTest: "Sertakan pesan uji",
    kpi: {
      deliveries: "Pengiriman",
      reached: "Sampai",
      problems: "Tidak sampai",
      messages: "Pesan",
      customers: "Customer",
    },
    kpiHint: {
      reached: "Terkirim atau diterima provider",
      problems: "Gagal, dilewati, atau bounce",
      customers: "{{count}} baru di periode ini",
    },
    vsPrevious: "dibanding periode sebelumnya",
    noPrevious: "Tidak ada data di periode sebelumnya",
    map: {
      title: "Sebaran customer",
      description:
        "Customer dan notifikasi yang tidak sampai per area. Angka pengiriman mengikuti periode yang dipilih.",
      customers: "Customer",
      metric: "Tampilkan",
      metricCustomers: "Customer",
      metricProblems: "Tidak sampai",
      areas: "Area teratas",
      area: "Area",
      coverage:
        "{{exact}} dengan koordinat, {{approximate}} ditempatkan di pusat kotanya, {{unlocated}} tanpa lokasi.",
      precision: { city: "Perkiraan: pusat kota", region: "Perkiraan: ibu kota provinsi" },
      empty: "Belum ada customer yang punya lokasi. Kirim kota atau koordinat saat mendaftarkan customer.",
      noWebgl: "Browser ini tidak bisa menampilkan peta karena WebGL nonaktif.",
      gestureWindows: "Gunakan Ctrl + scroll untuk memperbesar peta",
      gestureMac: "Gunakan ⌘ + scroll untuk memperbesar peta",
      gestureMobile: "Gunakan dua jari untuk menggeser peta",
      zoomIn: "Perbesar",
      zoomOut: "Perkecil",
      viewCustomers: "Lihat customer di {{area}}",
    },
    heatmap: {
      title: "Kapan notifikasi dikirim",
      description: "Pengiriman per hari dan jam, zona waktu {{tz}}.",
      volume: "Volume",
      problems: "Tidak sampai",
      metric: "Tampilkan",
      less: "Sedikit",
      more: "Banyak",
      cell: "{{day}}, {{hour}}: {{total}} pengiriman, {{problems}} tidak sampai",
      busiest: "Paling ramai: {{when}}",
      empty: "Tidak ada pengiriman di periode ini.",
    },
    daily: {
      title: "Pengiriman per hari",
      reached: "Sampai",
      problems: "Tidak sampai",
      tooltip: "{{date}}: {{total}} pengiriman, {{problems}} tidak sampai",
    },
    channels: {
      title: "Per channel",
      deliveries: "{{count}} pengiriman",
      rate: "{{rate}} sampai",
    },
    issues: {
      title: "Kenapa notifikasi tidak sampai",
      description: "Dikelompokkan per penyebab. Buka salah satu untuk melihat pengiriman yang terdampak.",
      none: "Semua notifikasi di periode ini sampai ke provider.",
      openLog: "Buka log pengiriman",
    },
    empty: "Tidak ada pengiriman di periode ini",
    emptyHint: "Angka akan muncul di sini setelah app mengirim pesan.",
  },
  customers: {
    cityFilter: "Area: {{city}}",
    clearCity: "Hapus filter area",
    title: "Customer",
    subtitle:
      "Pengguna yang didaftarkan app ke gateway, beserta alamat tempat mereka bisa menerima notifikasi.",
    searchPlaceholder: "ID customer, nama, email, atau nomor HP",
    search: "Cari customer",
    columns: {
      customer: "Customer",
      app: "App",
      channels: "Bisa dihubungi lewat",
      location: "Lokasi",
      lastActive: "Terakhir aktif",
    },
    empty: "Belum ada customer",
    emptyHint: "Customer muncul di sini setelah backend app mendaftarkannya lewat API.",
    apiReference: "Referensi API",
    noResults: "Tidak ada customer yang cocok dengan pencarian.",
    invalidCount: "{{count}} tidak bisa dipakai",
    subscriptionStatus: { active: "Aktif", unsubscribed: "Berhenti langganan", invalid: "Tidak valid" },
    locationSource: { backend: "Dari backend", ip: "Dari alamat IP", gps: "Dari GPS" },
    detail: {
      sendTest: "Kirim notifikasi uji",
      profile: "Profil",
      externalId: "ID customer",
      attributes: "Atribut",
      tags: "Tag",
      language: "Bahasa",
      timezone: "Zona waktu",
      location: "Lokasi",
      registered: "Terdaftar",
      lastActive: "Terakhir aktif",
      reach: "Alamat",
      reachHint:
        "Push dikirim ke semua perangkat yang aktif. Email, SMS, dan WhatsApp dikirim ke alamat utama tiap channel.",
      noSubscriptions: "Customer ini belum punya alamat, jadi belum ada notifikasi yang bisa sampai.",
      primary: "Utama",
      device: "Perangkat",
      lastSeen: "Terakhir terlihat {{when}}",
      unsubscribe: "Hentikan langganan",
      reactivate: "Aktifkan lagi",
      subscriptionUpdated: "Alamat diperbarui",
      activity: "{{days}} hari terakhir",
      lastDelivery: "Notifikasi terakhir {{when}}",
      history: "Riwayat notifikasi",
      historyHint:
        "Semua pengiriman ke customer ini, terbaru di atas. Buka salah satu untuk melihat apa yang terjadi dan langkah selanjutnya.",
      historyEmpty: "Belum ada notifikasi yang dikirim ke customer ini.",
      dangerZone: "Zona berbahaya",
      erase: "Hapus customer",
      eraseHint: "Untuk permintaan penghapusan data sesuai UU PDP. Profil dan semua alamat dihapus.",
      eraseTitle: "Hapus customer ini?",
      eraseBody:
        "Profil dan semua alamat dihapus. Pengiriman lama tetap dihitung di statistik, tetapi tanpa alamat dan isi pesan. Tindakan ini tidak bisa dibatalkan.",
      erased: "Customer dihapus",
    },
  },
  logs: {
    title: "Log pengiriman",
    subtitle: "Cari tahu kenapa seseorang menerima atau tidak menerima notifikasi.",
    searchPlaceholder: "ID customer, email, nomor HP, token push, atau ID pesan",
    search: "Cari pengiriman",
    filters: {
      app: "App",
      allApps: "Semua app",
      channel: "Channel",
      allChannels: "Semua channel",
      outcome: "Hasil",
      allOutcomes: "Semua hasil",
      period: "Periode",
      anyTime: "Kapan saja",
      reason: "Penyebab",
      anyReason: "Semua penyebab",
      toggle: "Filter",
      clear: "Hapus filter",
    },
    outcomes: { problems: "Tidak sampai", delivered: "Sampai", in_progress: "Sedang diproses" },
    periods: { "1": "24 jam terakhir", "7": "7 hari terakhir", "30": "30 hari terakhir" },
    columns: { time: "Waktu", recipient: "Penerima", message: "Pesan", channel: "Channel", result: "Hasil" },
    directAddress: "Alamat langsung",
    noAddress: "Tanpa alamat",
    empty: "Tidak ada pengiriman yang cocok dengan filter ini",
    emptyHint: "Coba periode yang lebih panjang atau hapus filter.",
    nothingYet: "Belum ada pengiriman",
    nothingYetHint: "Pengiriman muncul di sini begitu ada app yang mengirim pesan.",
    test: "Uji",
  },
  issues: {
    delivered: {
      label: "Terkirim",
      explain: "Provider memastikan pesan sudah sampai ke perangkat atau inbox.",
      action:
        "Tidak ada yang perlu diperbaiki. Kalau pengguna tidak melihatnya, cek pengaturan notifikasi di perangkatnya atau folder spam.",
    },
    accepted: {
      label: "Diterima provider",
      explain:
        "Provider sudah menerima pesan. Provider push tidak melaporkan apakah notifikasi benar-benar tampil di perangkat.",
      action:
        "Kalau pengguna tidak melihatnya, pastikan notifikasi app diizinkan, app tidak dibatasi penghemat baterai, dan pengguna login di perangkat itu.",
    },
    in_progress: {
      label: "Sedang diproses",
      explain: "Pesan sedang antre atau sedang dikirim.",
      action: "Tunggu sebentar. Halaman ini akan diperbarui sendiri.",
    },
    retrying: {
      label: "Dicoba ulang",
      explain:
        "Percobaan terakhir gagal karena gangguan sementara. Gateway mencoba lagi otomatis, sampai 5 kali.",
      action: "Belum perlu tindakan. Kalau banyak pengiriman terus dicoba ulang, cek status provider.",
    },
    customer_not_found: {
      label: "Customer belum terdaftar",
      explain: "App mengirim ke ID customer yang tidak dikenal gateway.",
      action:
        "Backend app harus mendaftarkan pengguna dengan ID customer yang persis sama sebelum mengirim. Cek salah ketik atau format ID yang berbeda.",
    },
    no_subscription: {
      label: "Tidak ada alamat untuk channel ini",
      explain:
        "Customer sudah terdaftar, tetapi tidak punya alamat untuk channel ini, jadi tidak ada tujuan pengiriman.",
      action:
        "Untuk push, minta pengguna membuka app dan mengizinkan notifikasi. Untuk email, SMS, atau WhatsApp, backend harus menambahkan alamatnya.",
    },
    subscription_invalid: {
      label: "Alamat sudah tidak valid",
      explain:
        "Pengiriman sebelumnya menunjukkan alamat ini sudah tidak berfungsi, misalnya app dihapus atau token push kedaluwarsa. Gateway tidak memakainya lagi.",
      action:
        "Untuk push, pengguna perlu membuka app lagi supaya token baru terdaftar. Untuk email, pastikan alamatnya ke pengguna, lalu aktifkan lagi di halaman customer.",
    },
    unsubscribed: {
      label: "Berhenti langganan",
      explain: "Customer memilih berhenti menerima pesan di channel ini.",
      action: "Hormati pilihan ini. Pengguna bisa berlangganan lagi dari app.",
    },
    address_invalid: {
      label: "Alamat ditolak",
      explain:
        "Provider menyatakan alamat ini tidak ada atau sudah tidak terdaftar. Gateway menandainya tidak valid supaya tidak dipakai lagi.",
      action:
        "Untuk push, app kemungkinan sudah dihapus atau tokennya kedaluwarsa; token baru terdaftar saat pengguna membuka app. Untuk email, cek salah ketik pada alamat.",
    },
    token_wrong_project: {
      label: "Token dari app lain",
      explain:
        "Token push ini dibuat oleh project Firebase yang berbeda dari credential yang dipakai untuk mengirim.",
      action:
        "Pastikan app memakai project Firebase yang benar, atau pasang credential FCM yang sesuai dengan alias yang dikirim app.",
    },
    credential_problem: {
      label: "Masalah credential",
      explain: "Provider menolak credential, atau tidak ada credential yang bisa dipakai untuk channel ini.",
      action:
        "Admin perlu menjalankan Uji koneksi di halaman Credential dan mengecek credential yang dipasang di app. Masalah ini berdampak ke semua pengguna, bukan hanya pengguna ini.",
    },
    provider_unavailable: {
      label: "Provider tidak tersedia",
      explain:
        "Provider tidak merespons atau melaporkan gangguan sementara, dan semua percobaan ulang gagal.",
      action:
        "Cek halaman status provider. Kirim ulang setelah pulih, atau pasang credential cadangan supaya pengiriman otomatis pindah.",
    },
    payload_rejected: {
      label: "Isi pesan ditolak",
      explain: "Provider menolak isi pesan, misalnya terlalu besar atau ada field yang tidak valid.",
      action: "App pengirim harus memperbaiki isi pesan. Detail teknis menunjukkan alasan dari provider.",
    },
    unknown_outcome: {
      label: "Hasil tidak diketahui",
      explain:
        "Worker berhenti saat mengirim, jadi tidak diketahui apakah pesan sudah keluar. Pesan tidak dikirim ulang otomatis supaya tidak dobel dan tidak tertagih dua kali.",
      action: "Kirim ulang secara manual kalau pesannya penting.",
    },
    bounced: {
      label: "Bounce",
      explain: "Pesan sempat diterima, lalu dikembalikan oleh server penerima.",
      action: "Pastikan alamatnya ke pengguna.",
    },
    other: {
      label: "Gagal",
      explain: "Provider mengembalikan error yang belum punya penjelasan di sini.",
      action: "Detail teknis di bawah menampilkan pesan asli dari provider.",
    },
  },
  delivery: {
    title: "Detail pengiriman",
    whatHappened: "Apa yang terjadi",
    whatToDo: "Yang perlu dilakukan",
    customer: "Customer",
    address: "Alamat",
    app: "App",
    channel: "Channel",
    message: "Pesan",
    content: "Isi",
    timeline: "Linimasa",
    technical: "Detail teknis",
    provider: "Provider",
    credential: "Alias credential",
    attempts: "Percobaan",
    providerMessageId: "ID pesan provider",
    errorCode: "Kode error",
    errorMessage: "Respons provider",
    payload: "Payload terkirim",
    events: "Event mentah",
    subscription: "Status alamat",
    deliveryId: "ID pengiriman",
    openMessage: "Buka pesan",
    openCustomer: "Buka customer",
    steps: {
      created: "Diterima gateway",
      sent: "Diserahkan ke provider",
      delivered: "Terkirim",
      failed: "Gagal",
      retry_scheduled: "Percobaan gagal, akan dicoba ulang",
      skipped: "Tidak dikirim",
      bounced: "Bounce",
      opened: "Dibuka",
      clicked: "Diklik",
      unsubscribed: "Berhenti langganan",
      retry_requested: "Retry diminta",
      retry_new_device: "Ditambahkan saat retry untuk perangkat yang lebih baru",
    },
    retryIn: "Percobaan berikutnya dalam {{seconds}} detik",
    retryBy: "oleh {{who}}",
    retryAddress: "sekarang ke {{address}}",
    retryCredential: "dengan credential {{alias}}",
  },
  account: {
    title: "Akun",
    subtitle: "Profil kamu dan cara kamu masuk.",
    profile: "Profil",
    name: "Nama tampilan",
    email: "Email",
    role: "Peran",
    organization: "Organisasi",
    profileSaved: "Profil disimpan",
    roles: { owner: "Pemilik", admin: "Admin", viewer: "Pembaca" },
    password: "Password",
    passwordHint: "Setelah diganti, semua perangkat lain tempat kamu login akan dikeluarkan.",
    currentPassword: "Password saat ini",
    newPassword: "Password baru",
    confirmPassword: "Ulangi password baru",
    minLength: "Minimal {{count}} karakter.",
    tooShort: "Gunakan minimal {{count}} karakter.",
    mismatch: "Password tidak sama.",
    changePassword: "Ganti password",
    passwordChanged: "Password diganti. Sesi kamu di perangkat lain sudah dikeluarkan.",
    show: "Tampilkan password",
    hide: "Sembunyikan password",
  },
  composer: {
    compose: "Tulis",
    json: "JSON",
    emails: "Alamat email",
    tokens: "Perangkat FCM",
    tokensHint: "Satu per baris. Firebase installation ID (FID) maupun token registrasi lama bisa dipakai.",
    title: "Judul",
    message: "Pesan",
    sendTest: "Kirim uji",
    priorityHint: {
      high: "OTP dan pesan mendesak",
      normal: "Sebagian besar pesan transaksional",
      low: "Broadcast dan promo",
    },
  },
  send: {
    title: "Kirim pesan",
    subtitle:
      "Kirim ke semua customer sebuah app, ke customer pilihan, atau langsung ke alamat. Preview menunjukkan apa yang akan dilihat penerima.",
    viewer: "Viewer tidak bisa mengirim pesan. Minta bantuan admin organisasi Anda.",
    noApps: "Tidak ada app yang aktif",
    noAppsHint: "Buat app, atau aktifkan app yang ditangguhkan, untuk mengirim dari dashboard.",
    app: "App",
    testMode: "Mode uji",
    testModeHint:
      "Memakai adapter mock. Tidak ada yang sampai ke provider sungguhan atau ke orang sungguhan.",
    audience: {
      title: "Penerima",
      all: "Semua customer",
      customers: "Customer",
      addresses: "Alamat",
      addressesHint: "Kirim tanpa customer terdaftar. Kolom muncul sesuai channel yang dipilih di bawah.",
      phones: "Nomor telepon",
      phonesHint: "Format E.164, satu per baris. Dipakai untuk SMS dan WhatsApp.",
    },
    customers: {
      search: "Cari customer",
      searchPlaceholder: "ID customer, nama, email atau nomor telepon",
      results: "Customer yang cocok",
      noMatch: "Tidak ada customer app ini yang cocok.",
      selectedLabel: "Customer terpilih",
      selected: "Terpilih: {{count}}",
      clear: "Kosongkan",
      paste: "Tempel ID",
      pasteLabel: "ID customer",
      pasteHint: "Pisahkan ID dengan koma, spasi atau baris baru.",
      addPasted: "Tambahkan ({{count}})",
    },
    reach: {
      pickChannel: "Pilih channel di bawah untuk melihat siapa yang terjangkau.",
      summary: "Menjangkau {{reachable}} dari {{total}} customer.",
      nobody: "Belum ada customer app ini yang punya alamat di channel ini.",
      devices: "{{customers}} customer di {{devices}} perangkat",
      customers: "{{customers}} customer",
      hint: "Customer tanpa alamat di sebuah channel dilewati di channel itu. Alamat yang berhenti langganan atau tidak valid tidak pernah dipakai.",
    },
    channelsHint: "Tiap channel butuh credential di credential profile app.",
    channel: { fcm: "Push (FCM)", email: "Email", sms: "SMS", whatsapp: "WhatsApp" },
    noCredential: "Belum ada credential untuk channel ini di profile app.",
    credential: "Credential",
    credentialHint: "Credential dari profile app yang mengirim channel ini.",
    credentialDefault: "Default (sesuai prioritas)",
    pushCredentialHint:
      "Perangkat yang terdaftar dengan credential-nya sendiri tetap memakai credential itu.",
    fcm: {
      image: "URL gambar",
      link: "Link (web)",
      linkHint: "Halaman HTTPS yang dibuka saat notifikasi web diklik.",
      data: "Data",
      dataHint: "key=value, satu per baris. Diteruskan ke aplikasi.",
    },
    email: {
      subject: "Subjek",
      fromName: "Nama pengirim",
      fromNameHint: "Kosongkan untuk memakai nama pengirim dari credential.",
      replyTo: "Reply-to",
      format: "Format",
      plain: "Teks biasa",
      html: "HTML",
      htmlHint:
        "Gunakan inline style: banyak aplikasi email mengabaikan blok <style> dan tidak pernah menjalankan script.",
      textAlt: "Versi teks biasa",
      textAltHint: "Opsional. Ditampilkan oleh aplikasi email yang tidak menampilkan HTML.",
      text: "Pesan",
    },
    sms: { text: "Pesan" },
    whatsapp: {
      template: "Nama template",
      templateHint: "Template yang sudah disetujui Meta.",
      language: "Bahasa",
      variables: "Variabel",
      variablesHint: "Satu per baris, sesuai urutan di template.",
    },
    delivery: "Pengiriman",
    categoryHint: "Misalnya transactional atau marketing. Broadcast wajib punya kategori.",
    review: "Tinjau dan kirim",
    preview: "Preview",
    previewHint: "Perkiraan: tiap perangkat dan aplikasi email menampilkan sedikit berbeda.",
    previewEmpty: "Pilih channel untuk melihat preview",
    errors: {
      channel: "Pilih minimal satu channel.",
      customers: "Pilih minimal satu customer.",
      addresses: "Tambahkan minimal satu alamat untuk channel yang dipilih.",
      category: "Broadcast wajib punya kategori.",
      data: "Tiap baris harus berbentuk key=value.",
      pushContent: "Isi judul, pesan, atau data.",
      https: "Gunakan link HTTPS.",
      subject: "Isi subjek.",
      emailBody: "Isi konten email.",
      smsText: "Isi pesannya.",
      template: "Isi nama template.",
      fix: "Perbaiki kolom yang ditandai dulu.",
    },
    confirm: {
      title: "Kirim pesan ini?",
      test: "Mode uji: ditangani adapter mock dan tidak ada yang dihubungi.",
      live: "Pesan ini langsung sampai ke orang sungguhan.",
      send: "Kirim sekarang",
      recipients: "Penerima",
      all: "Semua customer yang punya alamat di channel ini (sekitar {{n}})",
      allUnknown: "Semua customer yang punya alamat di channel ini",
      customers: "Customer: {{count}}",
      addresses: "Alamat langsung: {{count}}",
      broadcastWarning: "Ini broadcast. Pengiriman tidak bisa dihentikan setelah mulai.",
    },
  },
  preview: {
    channel: "Channel",
    yourApp: "Aplikasi Anda",
    now: "sekarang",
    dataOnly: "Pesan data saja: aplikasi menerimanya tanpa menampilkan notifikasi.",
    pushEmpty: "Isi judul atau pesan untuk melihat notifikasinya.",
    dataKeys: "Data:",
    email: {
      from: "Dari",
      fromCredential: "(alamat dari credential)",
      to: "Kepada",
      toRecipient: "Tiap penerima",
      replyTo: "Reply-to",
      subject: "Subjek",
      view: "Tampilan",
      views: { html: "HTML", text: "Teks biasa", source: "Source" },
      width: "Lebar",
      desktop: "Desktop",
      mobile: "Mobile",
      frameTitle: "Preview email",
      scaled: "Ditata pada lebar {{width}} px, ditampilkan {{percent}}%",
    },
    sms: { count: "{{chars}} karakter · {{parts}} bagian SMS", unicode: "Unicode, bagian lebih pendek" },
    whatsapp: {
      template: "Template",
      hint: "WhatsApp menampilkan teks template yang disetujui, dengan variabel ini diisi sesuai urutan.",
    },
  },
  retry: {
    open: "Retry",
    one: "Retry",
    title: "Retry delivery",
    description:
      "Mengirim ulang dengan credential dan alamat customer yang berlaku sekarang, jadi perbaiki dulu penyebabnya. Tidak ada yang dikirim sebelum Anda konfirmasi.",
    submit: "Retry {{count}}",
    submitPlain: "Retry",
    nothing: "Tidak ada yang bisa di-retry: tidak ada delivery gagal atau dilewati yang cocok.",
    summary: "{{count}} dari {{total}} delivery yang gagal atau dilewati bisa dikirim ulang.",
    actions: {
      resend: {
        title: "Kirim ulang",
        hint: "Konten sama. Customer menerimanya di alamat mereka saat ini, dan credential dipilih sesuai konfigurasi sekarang.",
      },
      recheck: {
        title: "Cek ulang alamat",
        hint: "Tadinya tidak ada alamat yang bisa dipakai. Hanya dikirim ke customer yang sekarang sudah punya, untuk push termasuk perangkat yang baru terdaftar.",
      },
      confirm: {
        title: "Hasil tidak diketahui",
        hint: "Worker berhenti saat mengirim, jadi pesan ini mungkin sudah sampai.",
      },
      never: {
        title: "Tidak di-retry",
        hint: "Retry tidak akan membantu: customer berhenti langganan, konten ditolak, atau alamat langsung tidak ada.",
      },
    },
    includeUnknown: "Kirim juga delivery yang hasilnya tidak diketahui",
    unknownWarning: "Pesan bisa sampai dua kali, dan SMS atau WhatsApp bisa tertagih dua kali.",
    credentials: {
      title: "Credential yang dipakai sekarang",
      hint: "Test connection dulu sebelum retry. Kalau credential-nya yang bermasalah, pilih credential lain di sini.",
      hintManyApps: "Delivery ini milik beberapa app. Untuk memilih credential lain, filter per app dulu.",
      deliveries: "Delivery: {{count}}",
      willUse: "Akan memakai",
      edited: "diubah {{when}}",
      error: "Saat ini tidak ada credential yang bisa dipakai, jadi delivery ini akan gagal lagi.",
      unchanged:
        "Credential ini sama dan belum diubah sejak delivery ini gagal karenanya. Perbaiki di halaman Credentials atau pilih yang lain sebelum retry.",
      use: "Kirim dengan",
      keep: "Sesuai permintaan tiap delivery",
      unusable: "Nonaktif atau provider tidak tersedia",
    },
    progress: "Progres retry",
    progressText: "{{done}} dari {{total}} diproses",
    done: "Dikirim ulang: {{count}}",
    doneUnchanged: "Dibiarkan apa adanya karena masih tanpa alamat yang bisa dipakai: {{count}}",
  },
  profiles: {
    title: "Profil credential",
    subtitle:
      "Profil adalah kumpulan credential bernama yang dipasang dengan alias. Setiap app mengirim dengan satu profil, jadi memindahkan app ke Mock atau Backup cukup satu kali ganti.",
    new: "Profil baru",
    createTitle: "Buat profil credential",
    editTitle: "Ubah profil",
    name: "Nama profil",
    namePlaceholder: "Production",
    description: "Deskripsi",
    descriptionPlaceholder: "Untuk apa profil ini",
    copyFrom: "Mulai dari",
    copyFromNone: "Profil kosong",
    copyFromHint: "Salin credential dari profil lain, lalu ganti yang perlu.",
    created: "Profil dibuat",
    saved: "Profil disimpan",
    deleted: "Profil dihapus",
    usedBy: "Dipakai oleh",
    noApps: "Belum ada app yang memakai profil ini.",
    credentials: "Credential",
    channels: "Channel",
    empty: "Belum ada profil credential",
    emptyHint:
      "Setiap app mendapat profil saat dibuat. Tambahkan profil di sini untuk menyiapkan set Mock atau Backup.",
    entries: "Credential di profil ini",
    entriesHint:
      "Client memilih credential lewat alias. Tanpa alias, nomor prioritas terkecil per channel yang dipakai, dan berikutnya mengambil alih kalau provider sedang gangguan.",
    shared: "Perubahan di sini langsung berlaku untuk semua app yang memakai profil ini: {{apps}}.",
    dangerZone: "Zona berbahaya",
    deleteHint: "Hanya profil yang tidak dipakai app yang bisa dihapus. Credential-nya tetap disimpan.",
    deleteTitle: "Hapus profil ini?",
    deleteBody: "Alias di profil ini dihapus. Credential-nya sendiri tetap disimpan.",
    inUse: "Pindahkan app yang memakai profil ini ke profil lain dulu.",
    app: {
      current: "App ini mengirim dengan",
      change: "Ganti profil",
      edit: "Ubah profil",
      changeTitle: "Ganti profil credential",
      changeHint: "Semua alias berpindah sekaligus. Perubahan sampai ke worker dalam beberapa detik.",
      target: "Profil baru",
      switched: "Profil credential diganti",
      missingAliases: "Tidak ada di profil baru, jadi request yang memakainya akan ditolak: {{aliases}}",
      missingChannels: "Tidak ada credential untuk channel ini di profil baru: {{channels}}",
      pushChanged:
        "Credential push berbeda untuk {{aliases}}. Token perangkat hanya berlaku untuk satu project Firebase, jadi token yang terdaftar dengan credential sekarang bisa berhenti berfungsi.",
      same: "Profil baru mencakup alias dan channel yang sama.",
      readOnly: "Untuk mengubah credential ini, ubah profilnya. Semua app yang memakainya ikut berubah.",
    },
  },
  status: {
    message: {
      scheduled: "Terjadwal",
      queued: "Antri",
      processing: "Diproses",
      completed: "Selesai",
      partially_failed: "Gagal sebagian",
      failed: "Gagal",
      cancelled: "Dibatalkan",
    },
    delivery: {
      pending: "Menunggu",
      sending: "Mengirim",
      sent: "Terkirim",
      delivered: "Diterima",
      failed: "Gagal",
      skipped: "Dilewati",
      bounced: "Bounce",
    },
    provider: { active: "Aktif", degraded: "Terganggu", disabled: "Nonaktif" },
    credential: { active: "Aktif", disabled: "Nonaktif" },
    app: { active: "Aktif", suspended: "Ditangguhkan" },
  },
  channels: {
    fcm: "FCM",
    apns: "APNs",
    webpush: "Web Push",
    email: "Email",
    sms: "SMS",
    whatsapp: "WhatsApp",
  },
  priority: { high: "Tinggi", normal: "Normal", low: "Rendah" },
  audit: {
    filter: "Jenis",
    allTypes: "Semua perubahan",
    detail: "Detail perubahan",
    field: "Field",
    before: "Sebelum",
    after: "Sesudah",
    noDiff: "Tidak ada perubahan field yang tercatat untuk aksi ini.",
    ip: "Alamat IP",
    actions: {
      create: "Dibuat",
      update: "Diubah",
      delete: "Dihapus",
      revoke: "Dicabut",
      erase: "Dihapus permanen",
      change_password: "Ganti password",
    },
    entities: {
      credential_profile: "Profil credential",
      profile_credential: "Isi profil",
      app: "App",
      api_key: "API key",
      credential: "Credential",
      app_credential: "Pemasangan credential",
      provider: "Provider",
      settings: "Pengaturan platform",
      customer: "Customer",
      subscription: "Alamat customer",
      user: "Pengguna",
    },
    title: "Audit log",
    subtitle: "Semua perubahan konfigurasi, terbaru di atas.",
    actor: "Siapa",
    action: "Aksi",
    entity: "Apa",
    when: "Kapan",
    changes: "Perubahan",
    empty: "Belum ada perubahan tercatat",
  },
  errors: {
    generic: "Terjadi kesalahan",
    network: "API tidak bisa dihubungi. Pastikan API berjalan dan VITE_API_BASE_URL mengarah ke sana.",
    notFound: "Halaman tidak ditemukan",
    notFoundHint: "Halaman yang kamu buka tidak ada.",
    codes: {
      profile_in_use: "Profil ini masih dipakai app. Pindahkan app ke profil lain dulu.",
      profile_name_taken: "Sudah ada profil dengan nama ini.",
      wrong_password: "Password saat ini salah.",
      same_password: "Pilih password yang berbeda dari password saat ini.",
      invalid_credentials: "Email atau password salah.",
      rate_limited: "Terlalu banyak percobaan. Coba lagi beberapa menit lagi.",
      forbidden: "Kamu tidak punya izin untuk ini.",
      unauthorized: "Sesi berakhir. Silakan masuk lagi.",
      slug_taken: "App dengan slug ini sudah ada.",
      alias_taken: "Alias ini sudah dipakai di app ini.",
      credential_in_use: "Credential ini masih di-assign ke app.",
      app_has_messages: "App ini sudah pernah mengirim pesan. Tangguhkan saja.",
      credential_channel_mismatch: "Credential itu untuk channel lain.",
      validation_error: "Ada isian yang tidak valid.",
    },
  },
};
